import React, { useState, useEffect } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
import { subscriptionsApi } from '../../lib/api';
import { 
  LayoutDashboard, 
  CreditCard, 
  PlusCircle, 
  Settings, 
  LogOut,
  Bell,
  ChevronRight,
  Sparkles,
  Menu,
  X,
  Calendar,
  AlertCircle,
  Lightbulb
} from 'lucide-react';
import { Button } from '../ui/button';
import { Avatar, AvatarFallback } from '../ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../ui/popover';

const DashboardLayout = ({ children }) => {
  const { user, logout } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [notificationsOpen, setNotificationsOpen] = useState(false);

  const isDemo = user?.is_demo || user?.email === 'demo@subguardhq.com';

  const navItems = [
    { to: '/dashboard', icon: LayoutDashboard, label: t.nav.dashboard },
    { to: '/subscriptions', icon: CreditCard, label: t.nav.subscriptions },
    { to: '/add-subscription', icon: PlusCircle, label: t.nav.addNew },
    { to: '/chat', icon: Sparkles, label: 'AI Assistant' },
    { to: '/feature-requests', icon: Lightbulb, label: 'Feature Requests' },
    { to: '/settings', icon: Settings, label: t.nav.settings },
  ];

  // Load upcoming renewals for notifications
  useEffect(() => {
    const loadNotifications = async () => {
      try {
        const response = await subscriptionsApi.getAll();
        const subs = response.data || [];
        
        // Find subscriptions renewing in the next 7 days
        const now = new Date();
        const sevenDaysFromNow = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);
        
        const upcoming = subs
          .filter(sub => {
            if (sub.status !== 'active' || !sub.renewal_date) return false;
            const renewalDate = new Date(sub.renewal_date);
            return renewalDate >= now && renewalDate <= sevenDaysFromNow;
          })
          .map(sub => ({
            id: sub.id,
            type: 'renewal',
            title: sub.name,
            message: `Renews on ${new Date(sub.renewal_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`,
            amount: sub.amount,
            date: sub.renewal_date,
            daysUntil: Math.ceil((new Date(sub.renewal_date) - now) / (1000 * 60 * 60 * 24))
          }))
          .sort((a, b) => new Date(a.date) - new Date(b.date));
        
        setNotifications(upcoming);
      } catch (error) {
        console.error('Failed to load notifications:', error);
      }
    };

    loadNotifications();
  }, []);

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const getInitials = (email) => {
    return email ? email.substring(0, 2).toUpperCase() : 'SG';
  };

  const handleNavClick = () => {
    setSidebarOpen(false);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(amount || 0);
  };

  return (
    <div className="min-h-screen bg-gray-50" data-testid="dashboard-layout">
      {/* Mobile overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`
        fixed left-0 top-0 z-50 h-screen w-64 bg-white border-r border-gray-100 shadow-soft
        transform transition-transform duration-300 ease-in-out
        lg:translate-x-0
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
      `}>
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="flex items-center justify-between px-6 py-5 border-b border-gray-100">
            <div className="flex items-center gap-2">
              <img 
                src="https://customer-assets.emergentagent.com/job_96339eda-de9f-4afd-b0cc-aa940ad2aaf2/artifacts/doc4booq_SubGuard_icon.png" 
                alt="SubGuard" 
                className="w-10 h-10 rounded-xl"
              />
              <span className="text-xl font-bold text-gray-900" style={{ fontFamily: 'Outfit' }}>
                SubGuard
              </span>
            </div>
            {/* Close button on mobile */}
            <button 
              className="lg:hidden p-2 rounded-lg hover:bg-gray-100"
              onClick={() => setSidebarOpen(false)}
            >
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-4 py-6 space-y-1 overflow-y-auto">
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                onClick={handleNavClick}
                data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-all duration-200 ${
                    isActive
                      ? 'bg-[#D1FAE5] text-[#064E3B]'
                      : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                  }`
                }
              >
                <item.icon className="w-5 h-5" strokeWidth={1.5} />
                {item.label}
              </NavLink>
            ))}
          </nav>

          {/* User section */}
          <div className="p-4 border-t border-gray-100">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button 
                  className="flex items-center gap-3 w-full px-3 py-2 rounded-xl hover:bg-gray-50 transition-colors"
                  data-testid="user-menu-trigger"
                >
                  <Avatar className="w-9 h-9 bg-[#D1FAE5]">
                    <AvatarFallback className="bg-[#D1FAE5] text-[#064E3B] text-sm font-medium">
                      {getInitials(user?.email)}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1 text-left min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">
                      {user?.email || 'User'}
                    </p>
                    <p className="text-xs text-gray-500">
                      {user?.subscription_plan 
                        ? `${user.subscription_plan.charAt(0).toUpperCase() + user.subscription_plan.slice(1)} plan`
                        : user?.has_quick_check 
                        ? 'Quick Check'
                        : 'Free plan'}
                    </p>
                  </div>
                  <ChevronRight className="w-4 h-4 text-gray-400 flex-shrink-0" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuItem onClick={() => { navigate('/settings'); handleNavClick(); }} data-testid="menu-settings">
                  <Settings className="w-4 h-4 mr-2" />
                  {t.nav.settings}
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} className="text-red-600" data-testid="menu-logout">
                  <LogOut className="w-4 h-4 mr-2" />
                  {t.nav.logout}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <main className="lg:pl-64 min-h-screen">
        {/* Header */}
        <header className="sticky top-0 z-30 glass border-b border-gray-100 px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            {/* Hamburger menu for mobile */}
            <button 
              className="lg:hidden p-2 rounded-lg hover:bg-gray-100"
              onClick={() => setSidebarOpen(true)}
              data-testid="mobile-menu-btn"
            >
              <Menu className="w-6 h-6 text-gray-600" />
            </button>
            
            {/* Logo for mobile header */}
            <div className="flex items-center gap-2 lg:hidden">
              <img 
                src="https://customer-assets.emergentagent.com/job_96339eda-de9f-4afd-b0cc-aa940ad2aaf2/artifacts/doc4booq_SubGuard_icon.png" 
                alt="SubGuard" 
                className="w-8 h-8 rounded-lg"
              />
              <span className="text-lg font-bold text-gray-900" style={{ fontFamily: 'Outfit' }}>
                SubGuard
              </span>
            </div>

            <div className="flex items-center gap-2">
              {/* Notifications */}
              <Popover open={notificationsOpen} onOpenChange={setNotificationsOpen}>
                <PopoverTrigger asChild>
                  <Button 
                    variant="ghost" 
                    size="icon" 
                    className="relative" 
                    data-testid="notifications-btn"
                  >
                    <Bell className="w-5 h-5 text-gray-600" strokeWidth={1.5} />
                    {notifications.length > 0 && (
                      <span className="absolute top-1 right-1 w-2 h-2 bg-[#EA580C] rounded-full"></span>
                    )}
                  </Button>
                </PopoverTrigger>
                <PopoverContent 
                  align="end" 
                  className="w-80 p-0"
                  sideOffset={8}
                >
                  <div className="p-4 border-b border-gray-100">
                    <h3 className="font-semibold text-gray-900" style={{ fontFamily: 'Outfit' }}>
                      Notifications
                    </h3>
                    <p className="text-xs text-gray-500 mt-0.5">
                      Upcoming renewals in the next 7 days
                    </p>
                  </div>
                  <div className="max-h-80 overflow-y-auto">
                    {notifications.length > 0 ? (
                      <div className="divide-y divide-gray-100">
                        {notifications.map((notification) => (
                          <div 
                            key={notification.id}
                            className="p-4 hover:bg-gray-50 cursor-pointer transition-colors"
                            onClick={() => {
                              setNotificationsOpen(false);
                              navigate('/subscriptions');
                            }}
                          >
                            <div className="flex items-start gap-3">
                              <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${
                                notification.daysUntil <= 1 
                                  ? 'bg-red-100' 
                                  : notification.daysUntil <= 3 
                                  ? 'bg-amber-100' 
                                  : 'bg-blue-100'
                              }`}>
                                <Calendar className={`w-5 h-5 ${
                                  notification.daysUntil <= 1 
                                    ? 'text-red-600' 
                                    : notification.daysUntil <= 3 
                                    ? 'text-amber-600' 
                                    : 'text-blue-600'
                                }`} />
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="font-medium text-gray-900 text-sm truncate">
                                  {notification.title}
                                </p>
                                <p className="text-xs text-gray-500 mt-0.5">
                                  {notification.message}
                                </p>
                                <div className="flex items-center gap-2 mt-1">
                                  <span className="text-xs font-medium text-gray-900">
                                    {formatCurrency(notification.amount)}
                                  </span>
                                  <span className={`text-xs px-1.5 py-0.5 rounded ${
                                    notification.daysUntil <= 1 
                                      ? 'bg-red-100 text-red-700' 
                                      : notification.daysUntil <= 3 
                                      ? 'bg-amber-100 text-amber-700' 
                                      : 'bg-blue-100 text-blue-700'
                                  }`}>
                                    {notification.daysUntil === 0 
                                      ? 'Today' 
                                      : notification.daysUntil === 1 
                                      ? 'Tomorrow' 
                                      : `${notification.daysUntil} days`}
                                  </span>
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="p-8 text-center">
                        <div className="w-12 h-12 bg-gray-100 rounded-xl flex items-center justify-center mx-auto mb-3">
                          <Bell className="w-6 h-6 text-gray-400" />
                        </div>
                        <p className="text-sm font-medium text-gray-900">All caught up!</p>
                        <p className="text-xs text-gray-500 mt-1">
                          No upcoming renewals in the next 7 days
                        </p>
                      </div>
                    )}
                  </div>
                  {notifications.length > 0 && (
                    <div className="p-3 border-t border-gray-100 bg-gray-50">
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        className="w-full text-[#064E3B] hover:text-[#053D2E] hover:bg-[#D1FAE5]"
                        onClick={() => {
                          setNotificationsOpen(false);
                          navigate('/subscriptions');
                        }}
                      >
                        View all subscriptions
                      </Button>
                    </div>
                  )}
                </PopoverContent>
              </Popover>
            </div>
          </div>
        </header>

        {/* Page content */}
        <div className="p-4 sm:p-6 lg:p-8">
          {children}
        </div>
      </main>
    </div>
  );
};

export default DashboardLayout;
